/**
 * Scrolling one element into view without moving the rest of the page.
 *
 * `Element.scrollIntoView` walks the whole ancestor chain and scrolls every scroller it
 * finds, and `overflow: hidden` doesn't opt an ancestor out — it stops the *user* scrolling,
 * not the browser. This app has such an ancestor: `.app-content` is `100vh` with hidden
 * overflow and holds a map inset by the header's height, so it carries a header's worth of
 * content it can never show. `scrollIntoView` takes that slack too and slides the page up
 * behind the fixed header, with no way back.
 */

/**
 * The nearest ancestor that scrolls by design.
 *
 * `auto` and `scroll` only. A hidden-overflow element can be scrolled programmatically, but
 * nothing about it says it is willing to be, and that distinction is the whole point here.
 */
function scrollingAncestor(element: HTMLElement): HTMLElement | null {
    for (let node = element.parentElement; node; node = node.parentElement) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
    }

    return null;
}

/**
 * Brings `element` to the top of that scroller and leaves every other one alone. No-ops when
 * nothing above it is willing to scroll.
 */
export function scrollToTopWithinScroller(element: HTMLElement): void {
    const scroller = scrollingAncestor(element);
    if (!scroller) return;

    // Offsets rather than `scrollIntoView`: the element's distance from the top of the
    // scroller is exactly where it should end up.
    const top =
        scroller.scrollTop +
        element.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top;

    const behavior: ScrollBehavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')
        ?.matches
        ? 'auto'
        : 'smooth';

    if (typeof scroller.scrollTo === 'function') {
        scroller.scrollTo({ top, behavior });
    } else {
        // Without the options form it still gets there, just without the easing.
        scroller.scrollTop = top;
    }
}
